import {
  ChoiceGroup,
  DefaultButton,
  IChoiceGroupOption,
  IconButton,
  Label,
  Modal,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  TextField,
  TooltipHost
} from '@fluentui/react';
import { Sparkle20Regular, ThumbDislike24Filled, ThumbLike24Filled } from '@fluentui/react-icons';
import { useEffect, useState } from 'react';
import localeService from 'src/dataServices/LocaleService';
import { feedbackService } from 'src/dataServices/FeedbackService';
import { ChatHistoryItem, ReasonCode } from 'src/state/contactus/types';

interface FeedbackModalProps {
  polarity: 'positive' | 'negative';
  responseId: string;
  threadId?: string;
  chatHistory?: ChatHistoryItem[];
  onSubmit: (
    reasonCode: string,
    reasonCodeLabel: string | undefined,
    reasonCodeDescription: string | undefined,
    comment: string
  ) => Promise<void>;
  onClose: () => void;
}

export function FeedbackModal({ polarity, responseId, threadId, chatHistory, onSubmit, onClose }: FeedbackModalProps) {
  const [reasonCodes, setReasonCodes] = useState<ReasonCode[]>([]);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [comment, setComment] = useState('');
  const [commentHint, setCommentHint] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [isDynamic, setIsDynamic] = useState(false);
  const [analysisContext, setAnalysisContext] = useState<string | undefined>();

  const loadReasonCodes = async () => {
    setIsLoading(true);
    setHasLoadError(false);
    try {
      const data = await feedbackService.getReasonCodes(responseId, polarity, threadId, chatHistory);
      setReasonCodes(data.reasonCodes);
      setCommentHint(data.commentHint);
      setIsDynamic(data.isDynamic);
      setAnalysisContext(data.analysisContext);
    } catch (error) {
      console.error('Failed to load reason codes:', error);
      setHasLoadError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReasonCodes();
  }, [polarity, responseId, threadId]);

  const handleSubmit = async () => {
    if (!selectedReason) return;

    const selectedReasonCode = reasonCodes.find((rc) => rc.id === selectedReason);
    const reasonCodeLabel = selectedReasonCode?.label;
    const reasonCodeDescription = selectedReasonCode?.description;

    setIsSubmitting(true);
    try {
      await onSubmit(selectedReason, reasonCodeLabel, reasonCodeDescription, comment);
      setIsSuccess(true);
      setTimeout(onClose, 1500);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = polarity === 'positive'
    ? (localeService.getText('ContactUs', 'FeedbackModalPositiveTitle'))
    : (localeService.getText('ContactUs', 'FeedbackModalNegativeTitle'));
  const IconComponent = polarity === 'positive' ? ThumbLike24Filled : ThumbDislike24Filled;

  const choiceGroupOptions: IChoiceGroupOption[] = reasonCodes.map((rc) => ({
    key: rc.id,
    text: rc.label,
    onRenderLabel: rc.description
      ? (props) => (
          <div>
            <span>{props?.text}</span>
            <div className="feedback-modal-radio-description">{rc.description}</div>
          </div>
        )
      : undefined
  }));

  return (
    <Modal isOpen onDismiss={onClose} isBlocking={false} containerClassName="feedback-modal-surface">
      <div style={{ position: 'relative' }}>
        <IconButton
          className="feedback-modal-close-button"
          iconProps={{ iconName: 'Cancel' }}
          ariaLabel={localeService.getText('ContactUs', 'FeedbackModalCloseAriaLabel')}
          onClick={onClose}
        />
        <div className="feedback-modal-header">
          <div
            className={`feedback-modal-header-icon ${
              polarity === 'positive' ? 'feedback-modal-header-icon--positive' : 'feedback-modal-header-icon--negative'
            }`}
          >
            <IconComponent />
          </div>
          <span style={{ fontSize: '20px', fontWeight: 600 }}>{title}</span>
        </div>

        <div className="feedback-modal-content">
          {isLoading || isSubmitting ? (
            <Spinner className="feedback-spinner" size={SpinnerSize.large} />
          ) : hasLoadError ? (
            <div className="feedback-modal-error">
              <span>{localeService.getText('ContactUs', 'FeedbackModalLoadError')}</span>
              <PrimaryButton onClick={loadReasonCodes}>{localeService.getText('ContactUs', 'FeedbackModalTryAgain')}</PrimaryButton>
            </div>
          ) : isSuccess ? (
            <div className="feedback-modal-success">
              <span className="feedback-modal-success-icon">✓</span>
              <span className="feedback-modal-success-text">{localeService.getText('ContactUs', 'FeedbackModalSuccessMessage')}</span>
            </div>
          ) : (
            <>
              <div className="feedback-modal-section">
                <div>
                  {isDynamic && (
                    <TooltipHost content={localeService.getText('ContactUs', 'FeedbackModalAISuggestedTooltip')}>
                      <span className="feedback-modal-ai-badge">
                        <Sparkle20Regular />
                        {localeService.getText('ContactUs', 'FeedbackModalAISuggestedBadge')}
                      </span>
                    </TooltipHost>
                  )}
                </div>
                {/* Wrap in a div to intercept Enter key — radio buttons natively respond to Space,
                    not Enter, so tabbing in and pressing Enter would do nothing without this. */}
                <div
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const focused = e.currentTarget.querySelector('input[type="radio"]:focus') as HTMLInputElement | null;
                      focused?.click();
                    }
                  }}
                >
                  <Label>
                    {localeService.getText('ContactUs', 'FeedbackModalSelectReasonLabel')} <span className="feedback-modal-required" aria-hidden="true">*</span>
                  </Label>
                  <ChoiceGroup
                    className="feedback-modal-radio-group"
                    selectedKey={selectedReason}
                    options={choiceGroupOptions}
                    onChange={(_, option) => option && setSelectedReason(option.key)}
                    required
                  />
                </div>
              </div>
              <div className="feedback-modal-section">
                <Label className="feedback-modal-section-label" htmlFor="feedback-additional-comments">
                  {localeService.getText('ContactUs', 'FeedbackModalAdditionalCommentsLabel')}
                </Label>
                <TextField
                  id="feedback-additional-comments"
                  className="feedback-modal-textarea"
                  multiline
                  rows={4}
                  resizable
                  value={comment}
                  onChange={(_, newValue) => setComment(newValue || '')}
                  placeholder={commentHint}
                />
                <span className="feedback-modal-disclaimer">
                  {localeService.getText('ContactUs', 'FeedbackModalCommentsDisclaimer')}
                </span>
              </div>
            </>
          )}
        </div>

        {!isLoading && !isSuccess && !hasLoadError && (
          <div className="feedback-modal-actions">
            <DefaultButton onClick={onClose}>{localeService.getText('ContactUs', 'CancelButton')}</DefaultButton>
            <PrimaryButton onClick={handleSubmit} disabled={!selectedReason || isSubmitting}>
              {isSubmitting ? (localeService.getText('ContactUs', 'SubmittingButton')) : (localeService.getText('ContactUs', 'FeedbackModalSubmitButton'))}
            </PrimaryButton>
          </div>
        )}
      </div>
    </Modal>
  );
}
